import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCms } from "../cms/CmsProvider";
import {
    getNewsList, formatDate, splitTitle, getCategory, getCategoryClass, getCategoryText, getExcerpt, getThumb, linkifyHtml,
} from "../cms/news";
import "../assets/styles/news.css";

/* カテゴリーの小さなラベル */
export function NewsTag({ item }) {
    const { i18n } = useTranslation();
    const cat = getCategory(item);
    return <span className={`news-tag ${getCategoryClass(cat)}`}>{getCategoryText(cat, i18n.language)}</span>;
}

/* サムネイル（画像が無い記事はやさしい色の代替表示） */
export function NewsThumb({ item, width = 640 }) {
    const { i18n } = useTranslation();
    const src = getThumb(item, width);
    // 縦長の画像（チラシなど）は切り抜かず全体を表示する
    const img = item?.image;
    const isTall = !!(img && img.width && img.height && img.height / img.width > 0.75);
    return (
        <div className={`news-card-thumb${isTall ? " is-tall" : ""}`}>
            {src ? (
                isTall ? (
                    <>
                        <img className="news-card-thumb-bg" src={src} alt="" aria-hidden="true" loading="lazy" />
                        <img className="news-card-thumb-fit" src={src} alt="" loading="lazy" />
                    </>
                ) : (
                    <img src={src} alt="" loading="lazy" />
                )
            ) : (
                <div className="news-card-thumb-empty" aria-hidden="true">
                    <span>{getCategoryText(getCategory(item), i18n.language)}</span>
                </div>
            )}
        </div>
    );
}

/* 一覧・トップで使うカード */
export function NewsCard({ item, showExcerpt = true }) {
    const { text } = splitTitle(item.title);
    return (
        <li className="news-card">
            <Link to={`/news/${item.id}`}>
                <NewsThumb item={item} />
                <div className="news-card-body">
                    <div className="news-card-meta">
                        <time>{formatDate(item.date)}</time>
                        <NewsTag item={item} />
                    </div>
                    <h3 className="news-card-title">{text}</h3>
                    {showExcerpt && item.body && (
                        <p className="news-card-excerpt">{getExcerpt(item.body)}</p>
                    )}
                </div>
            </Link>
        </li>
    );
}

export function NewsListPage() {
    const { site, loaded, isJa } = useCms("site");
    const news = getNewsList(site);

    return (
        <main className="news-page news-page--list">
            <div className="news-page-head">
                <h1 className="news-heading">{isJa ? "お知らせ" : "News"}</h1>
                <p className="news-lead">
                    {isJa
                        ? "活動報告やコラム、イベントのご案内をお届けします。"
                        : "Updates, columns and event information."}
                </p>
            </div>
            {!loaded ? (
                <p className="news-empty">{isJa ? "読み込み中…" : "Loading…"}</p>
            ) : news.length === 0 ? (
                <p className="news-empty">{isJa ? "現在お知らせはありません。" : "No news yet."}</p>
            ) : (
                <ul className="news-cards">
                    {news.map((n) => <NewsCard key={n.id} item={n} />)}
                </ul>
            )}
        </main>
    );
}

/* 記事下の「前の記事 / 次の記事」 */
function PagerLink({ item, dir, isJa }) {
    if (!item) return <div className={`news-pager-item is-empty is-${dir}`} aria-hidden="true" />;
    const label = dir === "prev"
        ? (isJa ? "← 前の記事" : "← Previous")
        : (isJa ? "次の記事 →" : "Next →");
    return (
        <Link to={`/news/${item.id}`} className={`news-pager-item is-${dir}`}>
            <NewsThumb item={item} width={320} />
            <div className="news-pager-text">
                <span className="news-pager-label">{label}</span>
                <span className="news-pager-title">{splitTitle(item.title).text}</span>
                <time>{formatDate(item.date)}</time>
            </div>
        </Link>
    );
}

export function NewsDetailPage() {
    const { id } = useParams();
    const { site, loaded, isJa } = useCms("site");
    const list = getNewsList(site);
    const index = list.findIndex((n) => n.id === id);
    const item = index >= 0 ? list[index] : null;
    const bodyHtml = useMemo(() => linkifyHtml(item?.body || ""), [item?.body]);

    if (!loaded) {
        return <main className="news-page"><p className="news-empty">{isJa ? "読み込み中…" : "Loading…"}</p></main>;
    }
    if (!item) {
        return (
            <main className="news-page">
                <p className="news-empty">{isJa ? "お知らせが見つかりませんでした。" : "News not found."}</p>
                <div className="news-back"><Link to="/news">{isJa ? "お知らせ一覧へ" : "All news"}</Link></div>
            </main>
        );
    }

    // 一覧は新しい順。前の記事 = ひとつ古い記事、次の記事 = ひとつ新しい記事
    const older = list[index + 1] || null;
    const newer = index > 0 ? list[index - 1] : null;
    const others = list.filter((n) => n.id !== item.id && n.id !== older?.id && n.id !== newer?.id).slice(0, 3);
    const { text } = splitTitle(item.title);

    return (
        <main className="news-page news-page--detail">
            <nav className="news-breadcrumb" aria-label="breadcrumb">
                <Link to="/">{isJa ? "ホーム" : "Home"}</Link>
                <span aria-hidden="true">›</span>
                <Link to="/news">{isJa ? "お知らせ" : "News"}</Link>
            </nav>

            <article className="news-article">
                <div className="news-card-meta">
                    <time>{formatDate(item.date)}</time>
                    <NewsTag item={item} />
                </div>
                <h1 className="news-article-title">{text}</h1>
                {item.image?.url && (
                    <img className="news-article-image" src={`${item.image.url}?w=1400&fm=webp&q=80`} alt={text} />
                )}
                {item.body && (
                    <div className="news-article-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                )}
            </article>

            <nav className="news-pager" aria-label={isJa ? "前後の記事" : "More articles"}>
                <PagerLink item={older} dir="prev" isJa={isJa} />
                <PagerLink item={newer} dir="next" isJa={isJa} />
            </nav>

            <div className="news-back">
                <Link to="/news">{isJa ? "お知らせ一覧へ" : "All news"}</Link>
            </div>

            {others.length > 0 && (
                <section className="news-related">
                    <h2 className="news-related-heading">{isJa ? "その他のお知らせ" : "More news"}</h2>
                    <ul className="news-cards news-cards--compact">
                        {others.map((n) => <NewsCard key={n.id} item={n} showExcerpt={false} />)}
                    </ul>
                </section>
            )}
        </main>
    );
}
